import React, { useState } from 'react';
import styled from 'styled-components';

const ControlsContainer = styled.div`
  position: fixed;
  top: 80px;
  left: 20px;
  width: 320px;
  background: rgba(0, 0, 0, 0.95);
  border: 2px solid #00ffff;
  border-radius: 8px;
  padding: 20px;
  z-index: 900;
  backdrop-filter: blur(10px);
`;

const Section = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  color: #00ffff;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 8px;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(0, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 255, 0.3);
  color: #ffffff;
  padding: 10px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #00ffff;
    background: rgba(0, 255, 255, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const Select = styled.select`
  width: 100%;
  background: rgba(0, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 255, 0.3);
  color: #ffffff;
  padding: 10px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #00ffff;
    background: rgba(0, 255, 255, 0.1);
  }

  option {
    background: #000000;
    color: #ffffff;
  }
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 255, 255, 0.2);

  &:last-child {
    border-bottom: none;
  }
`;

const StatLabel = styled.span`
  color: #aaaaaa;
  font-size: 12px;
`;

const StatValue = styled.span`
  color: #00ffff;
  font-size: 14px;
  font-weight: 600;
`;

const ToggleContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ToggleButton = styled.button`
  background: ${props => props.active ? '#00ffff' : 'transparent'};
  border: 2px solid #00ffff;
  color: ${props => props.active ? '#000000' : '#00ffff'};
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.2s;
  font-weight: 600;

  &:hover {
    background: #00ffff;
    color: #000000;
  }
`;

const Controls = ({ filters, onFilterChange, nodeCount, edgeCount }) => {
  const [localSearch, setLocalSearch] = useState(filters.searchTerm);

  const handleSearchChange = (e) => {
    setLocalSearch(e.target.value);
    onFilterChange({ searchTerm: e.target.value });
  };

  const handleGroupChange = (e) => {
    onFilterChange({ selectedGroup: e.target.value });
  };

  const handleEdgeTypeChange = (edgeType) => {
    onFilterChange({ showEdgeType: edgeType });
  };

  const edgeTypes = [
    { value: 'all', label: 'All' },
    { value: 'recursion_link', label: 'Recursion' },
    { value: 'semantic_link', label: 'Semantic' },
    { value: 'temporal_link', label: 'Temporal' },
    { value: 'structural_link', label: 'Structural' },
    { value: 'geometric_link', label: 'Geometric' },
    { value: 'lineage_link', label: 'Lineage' }
  ];

  return (
    <ControlsContainer>
      <Section>
        <Label>Search</Label>
        <Input
          type="text"
          placeholder="Search by title or keyword..."
          value={localSearch}
          onChange={handleSearchChange}
        />
      </Section>

      <Section>
        <Label>Node Group</Label>
        <Select value={filters.selectedGroup} onChange={handleGroupChange}>
          <option value="all">All Groups</option>
          <option value="ontology">Ontology</option>
          <option value="geometry">Geometry</option>
          <option value="lineage">Lineage</option>
          <option value="synthesis">Synthesis</option>
          <option value="general">General</option>
        </Select>
      </Section>

      <Section>
        <Label>Edge Type</Label>
        <ToggleContainer>
          {edgeTypes.map(type => (
            <ToggleButton
              key={type.value}
              active={filters.showEdgeType === type.value}
              onClick={() => handleEdgeTypeChange(type.value)}
            >
              {type.label}
            </ToggleButton>
          ))}
        </ToggleContainer>
      </Section>

      <Section>
        <Label>Graph Stats</Label>
        <StatsRow>
          <StatLabel>Nodes</StatLabel>
          <StatValue>{nodeCount}</StatValue>
        </StatsRow>
        <StatsRow>
          <StatLabel>Edges</StatLabel>
          <StatValue>{edgeCount}</StatValue>
        </StatsRow>
        <StatsRow>
          <StatLabel>Density</StatLabel>
          <StatValue>
            {nodeCount > 0 ? ((edgeCount / (nodeCount * (nodeCount - 1))) * 100).toFixed(1) + '%' : '0%'}
          </StatValue>
        </StatsRow>
      </Section>
    </ControlsContainer>
  );
};

export default Controls;